import React, { useMemo } from 'react';
import { useFindingsStore } from '../../stores/findings-store';
import { useEmailStore } from '../../stores/email-store';
import { useTodoStore } from '../../stores/todo-store';
import { useAppStore } from '../../stores/appStore';
import { SignaGlyph } from './SignaGlyph';
import { Citation } from './Citation';
import { PAText } from './PAText';
import { BriefingChip } from './BriefingChip';
import type { Finding } from '../../lib/findings-api';

/**
 * Morning briefing card - the anchor of the Today view.
 *
 * Reads the top 3 findings from the store, composes narrative prose with
 * inline citations, renders action chips, and shows a stats strap
 * (unread, due today, people waiting) at the bottom.
 */

interface BriefingCardProps {
  showCitations?: boolean;
}

export function BriefingCard({ showCitations = true }: BriefingCardProps) {
  const findings = useFindingsStore((s) => s.findings);
  const emails = useEmailStore((s) => s.emails);
  const todos = useTodoStore((s) => s.todos);
  const setActiveEmailId = useAppStore((s) => s.setActiveEmailId);
  const setActiveTodoId = useAppStore((s) => s.setActiveTodoId);
  const setActiveNoteId = useAppStore((s) => s.setActiveNoteId);

  const topFindings = useMemo(
    () =>
      [...findings]
        .filter((f) => !f.dismissed)
        .sort((a, b) => a.priority - b.priority)
        .slice(0, 3),
    [findings]
  );

  const unread = useMemo(
    () => emails.filter((e) => !e.isRead && !e.isArchived).length,
    [emails]
  );
  const dueToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return todos.filter((t) => {
      if (t.completedAt || !t.dueDate) return false;
      const d = new Date(t.dueDate);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    }).length;
  }, [todos]);
  const peopleWaiting = useMemo(() => {
    const set = new Set<string>();
    emails.forEach((e) => {
      if (!e.isRead && !e.isArchived) set.add(e.fromEmail);
    });
    return set.size;
  }, [emails]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);
  const dateLabel = useMemo(
    () => new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' }),
    []
  );

  const selectFinding = (f: Finding) => {
    if (!f.sourceEntity) return;
    if (f.sourceType === 'email') setActiveEmailId(f.sourceEntity);
    else if (f.sourceType === 'todo') setActiveTodoId(f.sourceEntity);
    else if (f.sourceType === 'note') setActiveNoteId(f.sourceEntity);
  };

  // Empty state - no findings, just a warm hello. Avoid saying "caught up" when
  // the raw counters still show unread mail, due todos, or people awaiting reply.
  if (topFindings.length === 0) {
    const headline = unread > 0 || dueToday > 0 || peopleWaiting > 0
      ? `${greeting}. No urgent findings yet; review the counts below for pending work.`
      : `${greeting}. Nothing urgent on your plate; you are caught up.`;

    return (
      <Shell dateLabel={dateLabel}>
        <Headline>{headline}</Headline>
        <Stats unread={unread} dueToday={dueToday} peopleWaiting={peopleWaiting} />
      </Shell>
    );
  }

  const lead = buildLeadSentence(greeting, topFindings);
  const bodyLine = buildBodyLine(topFindings, showCitations);

  return (
    <Shell dateLabel={dateLabel}>
      <Headline>{lead}</Headline>
      <div
        style={{
          fontSize: 14.5,
          lineHeight: 1.65,
          color: 'var(--ink-soft)',
          maxWidth: 620,
        }}
      >
        <PAText>{bodyLine}</PAText>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 22,
          flexWrap: 'wrap',
        }}
      >
        {topFindings.map((f, i) => (
          <BriefingChip
            key={f.id}
            label={f.actionLabel}
            onClick={() => selectFinding(f)}
            accent={i === 0}
          />
        ))}
      </div>

      <Stats unread={unread} dueToday={dueToday} peopleWaiting={peopleWaiting} />
    </Shell>
  );
}

// Layout helpers

function Shell({
  children,
  dateLabel,
}: {
  children: React.ReactNode;
  dateLabel: string;
}) {
  return (
    <div
      style={{
        margin: '32px 0 28px',
        padding: '28px 32px 24px',
        background: 'var(--briefing-bg)',
        borderRadius: 18,
        border: '1px solid var(--rule)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at top right, var(--accent-wash) 0%, transparent 55%)',
          pointerEvents: 'none',
          opacity: 0.7,
        }}
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '1.4px',
            textTransform: 'uppercase',
            color: 'var(--ink-faint)',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <SignaGlyph size={12} />
          <span>Your morning briefing</span>
          <span
            aria-hidden
            style={{
              width: 1,
              height: 10,
              background: 'var(--rule)',
            }}
          />
          <span>{dateLabel}</span>
        </div>
        {children}
      </div>
    </div>
  );
}

function Headline({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 26,
        lineHeight: 1.2,
        letterSpacing: '-0.7px',
        color: 'var(--ink)',
        fontWeight: 600,
        marginBottom: 14,
        maxWidth: 560,
      }}
    >
      {children}
    </div>
  );
}

function Stats({
  unread,
  dueToday,
  peopleWaiting,
}: {
  unread: number;
  dueToday: number;
  peopleWaiting: number;
}) {
  return (
    <div
      style={{
        marginTop: 22,
        paddingTop: 16,
        borderTop: '1px dashed var(--rule)',
        display: 'flex',
        gap: 24,
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        color: 'var(--ink-faint)',
      }}
    >
      <span>
        {unread} unread
      </span>
      <span>
        {dueToday} due today
      </span>
      <span>
        {peopleWaiting} awaiting reply
      </span>
    </div>
  );
}

// Prose composition

function buildLeadSentence(greeting: string, top: Finding[]): string {
  const count = top.length;
  if (count === 1) {
    return `${greeting}. One thing is time-sensitive today.`;
  }
  const emphasisName = top[0]?.title.split(' ')[0] ?? '';
  if (count === 2) {
    return `${greeting}. ${emphasisName} is the time-sensitive one today, and one more thing is waiting on you.`;
  }
  return `${greeting}. ${count} things are time-sensitive today.`;
}

function buildBodyLine(top: Finding[], showCitations: boolean): React.ReactNode {
  const parts: React.ReactNode[] = [];
  top.forEach((f, i) => {
    parts.push(
      <React.Fragment key={f.id}>
        <span>{f.description}</span>
        {' '}
        <Citation
          source={f.sourceType}
          date={formatFindingDate(f.createdAt)}
          show={showCitations}
          title={f.title}
        />
        {i < top.length - 1 && ' '}
      </React.Fragment>
    );
  });
  return <>{parts}</>;
}

function formatFindingDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
